import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Mail, Loader2, TrendingUp, TrendingDown, Info, Trophy, Crown, Send, Sparkles, IndianRupee, CalendarOff, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { StarDashLayout } from "@/pages/star/StarLayout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MyFees from "@/pages/star/MyFees";
import StudentLeave from "@/pages/star/StudentLeave";
import MyProfile from "@/pages/star/MyProfile";
import StudentDiary from "@/pages/star/diary/StudentDiary";
import { NotebookPen } from "lucide-react";
import ForceChangePassword from "@/pages/star/ForceChangePassword";
import Messages, { useUnreadMessages } from "@/pages/star/Messages";
import AttendanceRangeView from "@/pages/star/AttendanceRangeView";
import { MessageCircle, CalendarCheck } from "lucide-react";

const statusStyle = (s) => s === "approved" ? "bg-green-600" : s === "rejected" ? "bg-red-600" : "bg-amber-500";

const STUDENT_TABS = [
  { value: "diary", testid: "tab-my-diary", label: "My Diary", Icon: NotebookPen, tile: "bg-yellow-100 text-yellow-700" },
  { value: "stars", testid: "tab-my-stars", label: "My Stars", Icon: Star, tile: "bg-amber-100 text-amber-700" },
  { value: "messages", testid: "tab-messages", label: "Messages", Icon: MessageCircle, tile: "bg-violet-100 text-violet-700" },
  { value: "fees", testid: "tab-my-fees", label: "My Fees", Icon: IndianRupee, tile: "bg-emerald-100 text-emerald-700" },
  { value: "attendance", testid: "tab-my-attendance", label: "Attendance", Icon: CalendarCheck, tile: "bg-sky-100 text-sky-700" },
  { value: "leave", testid: "tab-my-leave", label: "Leave", Icon: CalendarOff, tile: "bg-rose-100 text-rose-700" },
  { value: "profile", testid: "tab-my-profile", label: "My Profile", Icon: UserCircle2, tile: "bg-indigo-100 text-indigo-700" },
];

const navCardCls = "group relative flex h-auto w-full flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md data-[state=active]:border-transparent data-[state=active]:bg-gradient-to-br data-[state=active]:from-blue-900 data-[state=active]:to-sky-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-900/25";

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const unread = useUnreadMessages();
  useEffect(() => {
    if (user && user.role === "student" && user.token_scope === "onboarding") {
      navigate("/student/onboarding", { replace: true });
    }
  }, [user, navigate]);
  const [txns, setTxns] = useState([]);
  const [board, setBoard] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(null);
  const [stars, setStars] = useState(1);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [t, b, r] = await Promise.all([
        api.get("/student/transactions"),
        api.get("/student/leaderboard"),
        api.get("/student/star-requests"),
      ]);
      setTxns(t.data);
      setBoard(b.data);
      setRequests(r.data);
    } catch { toast.error("Could not load your activity"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const requestReason = async (tid) => {
    setRequesting(tid);
    try {
      const { data } = await api.post(`/student/transactions/${tid}/request-reason`);
      toast.success(data.message || "Email sent");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send email");
    } finally { setRequesting(null); }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    const n = parseInt(stars, 10) || 0;
    if (n < 1) { toast.error("Enter how many stars you are requesting"); return; }
    if (reason.trim().length < 3) { toast.error("Please give a clear reason"); return; }
    setSubmitting(true);
    try {
      await api.post("/student/star-requests", { stars: n, reason: reason.trim() });
      toast.success("Request submitted — your teacher will review it. Your balance is unchanged until approved.");
      setStars(1); setReason("");
      const { data } = await api.get("/student/star-requests");
      setRequests(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not submit request");
    } finally { setSubmitting(false); }
  };

  const balance = user?.star_balance ?? 0;
  const fmt = formatDateTime;

  // Forced password change after an admin-assisted reset — blocks the portal until done.
  if (user?.must_change_password) return <ForceChangePassword />;

  return (
    <StarDashLayout title="Student Portal" testid="student-dashboard" headerGradient hero={(
      <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid="student-welcome">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-sky-600 text-white text-lg font-extrabold shadow-md shadow-blue-900/20">{(user?.name || user?.email || "S").charAt(0).toUpperCase()}</div>
        <div className="min-w-0">
          <p className="text-xs text-slate-500">Welcome back,</p>
          <p className="text-lg font-extrabold text-slate-900 leading-tight truncate">{user?.name || user?.email}</p>
          {user?.roll_number && <p className="text-xs text-slate-400">Roll {user.roll_number}</p>}
        </div>
        <span className="sm:ml-auto inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-sm font-extrabold text-amber-700 ring-1 ring-amber-200" data-testid="header-balance">
          <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {balance}★
        </span>
      </div>
    )}>
      <Seo title="My Stars" />
      <Tabs defaultValue="stars" className="mt-2">
        <TabsList className="mb-8 grid w-full grid-cols-2 sm:grid-cols-3 gap-3 h-auto bg-transparent p-0">
          {STUDENT_TABS.map(({ value, testid, label, Icon, tile }) => (
            <TabsTrigger key={value} value={value} data-testid={testid} className={navCardCls}>
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tile} transition-colors group-data-[state=active]:bg-white/20 group-data-[state=active]:text-white`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-sm font-bold leading-tight">{label}</span>
              {value === "messages" && unread > 0 && <span className="absolute top-3 right-3 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[10px] font-bold group-data-[state=active]:bg-white group-data-[state=active]:text-blue-700" data-testid="messages-tab-badge">{unread}</span>}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="stars">
      <div className="rounded-3xl bg-gradient-to-br from-blue-900 to-sky-700 text-white p-8 shadow-xl flex items-center justify-between" data-testid="student-balance-card">
        <div>
          <p className="text-sky-200 font-semibold uppercase tracking-widest text-xs">Your Star Balance</p>
          <p className="mt-2 text-5xl font-extrabold flex items-center gap-3" data-testid="student-balance-value">
            <Star className="h-10 w-10 text-amber-400 fill-amber-400" /> {balance}
          </p>
        </div>
        <Star className="h-24 w-24 text-white/10 fill-white/10 hidden sm:block" />
      </div>

      {/* Request a Star */}
      <div className="mt-8 rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="student-request-card">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-amber-500" />
          <h2 className="text-xl font-bold text-slate-900">Request a Star</h2>
        </div>
        <p className="text-sm text-slate-500 mb-5">Ask your teacher to award stars. Your request stays pending and does not change your balance until a teacher approves it.</p>
        <form onSubmit={submitRequest} className="space-y-4" data-testid="request-star-form">
          <div className="max-w-[180px]">
            <Label className="mb-1.5 block font-semibold text-slate-700">Stars requested</Label>
            <Input type="number" min="1" max="100" value={stars} onChange={(e) => setStars(e.target.value)} data-testid="request-star-amount" required />
          </div>
          <div>
            <Label className="mb-1.5 block font-semibold text-slate-700">Reason</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. I scored full marks in the Maths test" data-testid="request-star-reason" required />
          </div>
          <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-950" data-testid="request-star-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Submit request</>}
          </Button>
        </form>

        {requests.length > 0 && (
          <div className="mt-6" data-testid="student-requests-list">
            <h3 className="font-bold text-slate-900 mb-3">My Requests</h3>
            <div className="space-y-2">
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl border border-slate-200 p-3 flex items-center gap-3" data-testid={`student-request-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{r.reason}</p>
                    <p className="text-xs text-slate-500">Requested {r.requested_stars}★{r.approved_stars != null ? ` · Approved ${r.approved_stars}★` : ""} · {fmt(r.created_at)}</p>
                  </div>
                  <Badge className={statusStyle(r.status)} data-testid={`student-request-status-${r.id}`}>{r.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Standard-wise leaderboard */}
      {board && (
        <div className="mt-8 rounded-3xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="student-leaderboard">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h2 className="text-xl font-bold text-slate-900">Top 3 in {board.standard || "your class"}</h2>
          </div>
          <p className="text-sm text-slate-500 mb-5">Ranked by current star balance. Ties share the same rank.</p>

          {board.top3.length === 0 ? (
            <p className="text-sm text-slate-500">No ranked students in your standard yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {board.top3.map((s, i) => (
                <div key={i} className={`rounded-2xl p-4 text-center border ${s.rank === 1 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid={`student-board-top-${i}`}>
                  <div className={`mx-auto flex h-9 w-9 items-center justify-center rounded-full font-bold ${s.rank === 1 ? "bg-amber-400 text-amber-950" : "bg-blue-900 text-white"}`}>
                    {s.rank === 1 ? <Crown className="h-5 w-5" /> : s.rank}
                  </div>
                  <p className="mt-2 font-bold text-slate-900 leading-tight">{s.name}</p>
                  <p className="mt-1 inline-flex items-center gap-1 text-amber-600 font-bold"><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{s.star_balance}</p>
                </div>
              ))}
            </div>
          )}

          <div className={`mt-5 rounded-2xl px-5 py-4 flex items-center justify-between ${board.in_top3 ? "bg-green-50 border border-green-200" : "bg-sky-50 border border-sky-200"}`} data-testid="student-own-rank">
            <div>
              <p className="text-sm font-semibold text-slate-700">
                Your Rank: <span className="font-extrabold text-blue-900" data-testid="student-own-rank-value">{board.your_rank}</span> out of {board.total} in {board.standard || "your class"}
              </p>
              {board.in_top3 && <p className="text-xs text-green-700 font-semibold mt-0.5">🎉 You're in the Top 3!</p>}
            </div>
            <span className="flex items-center gap-1.5 font-extrabold text-amber-600"><Star className="h-5 w-5 fill-amber-400 text-amber-400" />{board.your_balance}</span>
          </div>
        </div>
      )}

      <h2 className="mt-10 mb-4 text-xl font-bold text-slate-900">Star Activity</h2>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>
      ) : txns.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500" data-testid="student-no-txns">
          <Info className="h-8 w-8 mx-auto mb-2 text-slate-400" />
          No star activity yet. Your teachers will add stars for your progress.
        </div>
      ) : (
        <div className="space-y-3" data-testid="student-txn-list">
          {txns.map((t) => {
            const positive = t.delta > 0;
            return (
              <div key={t.id} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`student-txn-${t.id}`}>
                <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${positive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {positive ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900">{t.reason}</p>
                  <p className="text-sm text-slate-500">By {t.teacher_name} · {fmt(t.created_at)}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Balance: {t.balance_before} → {t.balance_after}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xl font-extrabold ${positive ? "text-green-600" : "text-red-600"}`}>{positive ? "+" : ""}{t.delta}</span>
                  <Button variant="outline" size="sm" onClick={() => requestReason(t.id)} disabled={requesting === t.id} data-testid={`request-reason-${t.id}`}>
                    {requesting === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1.5" /> Request Reason</>}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>
        <TabsContent value="messages"><Messages scope="student" /></TabsContent>
        <TabsContent value="diary"><StudentDiary /></TabsContent>
        <TabsContent value="fees"><MyFees /></TabsContent>
        <TabsContent value="attendance"><AttendanceRangeView studentId={user?.id} /></TabsContent>
        <TabsContent value="leave"><StudentLeave /></TabsContent>
        <TabsContent value="profile"><MyProfile /></TabsContent>
      </Tabs>
    </StarDashLayout>
  );
}
